import * as Ticketure from '@/types/ticketure-api.types'
import {
  getRemainingCapacity,
  getTicketVisibility,
  shouldBeExcluded,
  sortByDisplayOrder,
  transformAdditionalInfo,
} from '@/utils/transformers.utils'
import { getMetaDataForCategory } from '@/data-access/transformers/transformMeta'
import { transformSalesWindow } from '@/data-access/transformers/transformSalesWindow'

/**
 * Filters out ticket groups for any group where metadata to exclude the ticket group is enabled
 * @param ticketGroupList
 * @param metaData
 */
export const filterTicketGroups = (
  ticketGroupList: Ticketure.TicketGroupDatum[],
  metaData: Transformed.MetaData[],
): Ticketure.TicketGroupDatum[] => ticketGroupList.filter(
  (ticketGroup) => {
    const metaList = getMetaDataForCategory(ticketGroup.id, metaData)
    const hasMetadata = shouldBeExcluded(metaList)
    return !hasMetadata
  },
)

/**
 *
 * @param {string | undefined} incomingType datatype for additional info
 * @param {string[]} [options] list of options for select type
 * @returns {AdditionalInfoType | null} typed info type
 */
export const transformAdditionalInfoType = (
  incomingType: string | undefined,
  options?: string[],
): AdditionalInfoType | null => {
  switch (incomingType) {
  case 'string':
    return 'String'
  case 'integer':
    return 'Int'
  case 'boolean':
    return 'Boolean'
  case undefined:
  default:
    if (!incomingType && !!options) {
      return 'Select'
    }
    return null
  }
}

/**
 * Get a list of unique category ids from a list of ticket tiers
 * all categories represented by tiers
 * @param {Transformed.TicketTier[]} tiers a list of ticket tiers
 * @returns {string[]} category ids
 */
export const getUniqueCategoryIdsFromTiers = (tiers: Transformed.TicketTier[]): string[] => (
  [...(new Set(tiers.map((tier) => tier.categoryId)))]
)

type TransformTicketCategoryParams = {
  categories?: Ticketure.TicketGroup,
  metaData: Transformed.MetaData[],
  events?: Ticketure.EventTemplate,
  sellers?: Ticketure.Seller,
  sessions?: Ticketure.EventSession,
}

/**
 * Transform Ticketure ticket category list into a structured array of TicketCategory objects.
 * @param {TransformTicketCategoryParams} params
 * @param {Ticketure.TicketGroupList | undefined} params.categoryList
 * @param {MetaData[]} params.metaData
 * @param {Ticketure.EventTemplateList} params.events
 * @param {Ticketure.SellerList} params.sellers
 * @param {Ticketure.EventSessionList} params.sessions
 */
export const transformTicketCategory = ({
  categories,
  metaData,
  events,
  sellers,
  sessions,
}: TransformTicketCategoryParams): Transformed.TicketCategory[] => {
  const data = categories ? categories._data : []
  const filteredData = filterTicketGroups(data, metaData)

  return sortByDisplayOrder(filteredData.map((ticketGroup) => ({
    id: ticketGroup.id,
    eventId: ticketGroup.event_template_id,
    portalId: ticketGroup.portal_id,
    title: ticketGroup.name,
    subtitle: ticketGroup.description,
    visibility: getTicketVisibility(ticketGroup.hidden_type),
    displayOrder: ticketGroup._rank,
    maxPerOrder: ticketGroup.max_tickets_per_order,
    capacity: {
      total: ticketGroup.capacity,
      remaining: getRemainingCapacity(ticketGroup),
      oversell: ticketGroup.oversell_capacity,
    },
    additionalInfo: transformAdditionalInfo(ticketGroup.additional_info_spec, ticketGroup.id),
    salesWindow: transformSalesWindow({
      ticketGroup,
      events,
      sellers,
      sessions,
    }),
    soldOut: !!ticketGroup.sold_out,
    handler: ticketGroup.handler,
    meta: getMetaDataForCategory(ticketGroup.id, metaData),
  } satisfies Transformed.TicketCategory)))
}

/**
 * Check if a category has associated tiers
 * @param {string[]} categoryIdsFromTiers
 * @param {string} categoryId
 */
const hasAssociatedTiers = (categoryIdsFromTiers: string[], categoryId: string): boolean => (
  categoryIdsFromTiers.includes(categoryId)
)

/**
 * Check if a category has available capacity
 * @param {TicketCategory} category
 */
const hasAvailableCapacity = (category: Transformed.TicketCategory): boolean => (
  category.capacity.total !== 0
)

/**
 * Check if a category has an active sales window
 * @param {TicketCategory} category
 */
const hasActiveSalesWindow = (category: Transformed.TicketCategory): boolean => (
  category.salesWindow.length === 0
  || category.salesWindow.some((sw) => !sw || sw.active)
)

/**
 * Remove unwanted categories
 *
 * - If a category does not have associated tiers
 * - If a category capacity is 0
 * - If category sales window is not active
 *
 * Filter meta data based on available categories
 * i.e. only include metadata for available categories
 *
 * @param {TicketCategory[]} categories a list of ticket categories
 * @param {Transformed.TicketTier[]} tiers a list of ticket tiers
 * @param {MetaData[]} metaData a list of meta data
 * @returns {Object} filteredData
 * @returns {TicketCategory[]} filteredData.categories a list of filtered ticket categories
 * @returns {MetaData[]} filteredData.metaData a list of filtered meta data
 */
export const filterCategoryAndMetaData = (
  categories: Transformed.TicketCategory[],
  tiers: Transformed.TicketTier[],
  metaData: Transformed.MetaData[],
): {
  categories: Transformed.TicketCategory[],
  metaData: Transformed.MetaData[],
} => {
  const categoryIdsFromTiers = getUniqueCategoryIdsFromTiers(tiers)

  // make sure we only deliver categories with capacity and tiers
  const nextCategories = categories
    .filter((category) => (
      // categories are represented by tiers
      hasAssociatedTiers(categoryIdsFromTiers, category.id)
        // capacity is not 0, could be a positive int, or -1 to indicate unlimited
        && hasAvailableCapacity(category)
        // sales-window is active
        && hasActiveSalesWindow(category)))

  // category ids from filtered categories
  const nextCategoryIds = nextCategories.map((category) => category.id)

  // make sure we only deliver metadata for active categories
  // and return all meta data for all other resources
  const nextMetaData = metaData.filter((meta) => (
    // if the resource type is not ticket_group
    // or is ticket_group and contains one of the filtered category ids
    meta.resource !== 'ticket_group' || nextCategoryIds.includes(meta.resourceId)
  ))

  return {
    categories: nextCategories,
    metaData: nextMetaData,
  }
}
